// src/components/ProjectExecution/DocumentComments.tsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ExpandMore, Comment } from '@mui/icons-material';
import { format } from 'date-fns';
import { apiService } from '../../services/api';

interface DocumentComment {
  actionId: number;
  actionType: string;
  comments: string;
  commentedAt: string;
  commentedBy: {
    userId: number;
    email: string;
    displayName: string;
  } | null;
}

interface DocumentCommentsProps {
  file: {
    spItemId?: number | null;
    siteId: string;
    driveId: string;
    itemId: string;
  } | null;
  /** Sprint G3: when viewing an old document version, pass its version label to load comments for that version */
  versionLabel?: string | null;
}

export function DocumentComments({ file, versionLabel }: DocumentCommentsProps) {
  const [comments, setComments] = useState<DocumentComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [versionLabels, setVersionLabels] = useState<string[]>([]);
  const [selectedVersionLabel, setSelectedVersionLabel] = useState<string | null>(null);

  const effectiveVersionLabel = selectedVersionLabel ?? versionLabel ?? undefined;

  useEffect(() => {
    if (file) {
      fetchComments(file, effectiveVersionLabel);
    } else {
      setComments([]);
      setError(null);
      setVersionLabels([]);
      setSelectedVersionLabel(null);
    }
  }, [file, effectiveVersionLabel]);

  useEffect(() => {
    if (file?.spItemId) {
      setSelectedVersionLabel(null);
      apiService.getDocumentCommentVersionLabels(file.spItemId).then((data) => {
        setVersionLabels(data.versionLabels || []);
      }).catch(() => setVersionLabels([]));
    } else {
      setVersionLabels([]);
    }
  }, [file?.spItemId]);

  const loadVersionLabelsFromResponse = (spItemIdFromResponse: number | null) => {
    if (spItemIdFromResponse != null) {
      apiService.getDocumentCommentVersionLabels(spItemIdFromResponse).then((data) => {
        setVersionLabels(data.versionLabels || []);
      }).catch(() => setVersionLabels([]));
    }
  };

  const fetchComments = async (
    fileMetadata: { spItemId?: number | null; siteId: string; driveId: string; itemId: string },
    versionLabelParam?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (fileMetadata.spItemId) {
        response = await apiService.getDocumentComments(fileMetadata.spItemId, versionLabelParam);
      } else {
        response = await apiService.getDocumentCommentsBySharePointId(
          fileMetadata.siteId,
          fileMetadata.driveId,
          fileMetadata.itemId,
          versionLabelParam
        );
      }
      
      // Sprint J: Defensive deduplication by content only (actionType + actionId + comment text) so same comment from two sources with different timestamp/user collapses
      const seenKeys = new Set<string>();
      const deduped = response.comments.filter((c) => {
        const key = `${c.actionType}|${Math.abs(c.actionId)}|${(c.comments || '').trim()}`;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });
      // Sort comments by commentedAt descending (most recent first)
      const sortedComments = [...deduped].sort((a, b) =>
        new Date(b.commentedAt || 0).getTime() - new Date(a.commentedAt || 0).getTime()
      );

      setComments(sortedComments);

      // When we loaded by (siteId, driveId, itemId), response may include spItemId; use it to load legacy version labels for "Show comments for" dropdown
      const resSpItemId = response && typeof response === 'object' && 'spItemId' in response ? (response as { spItemId?: number | null }).spItemId : null;
      if (resSpItemId != null && !fileMetadata.spItemId) {
        loadVersionLabelsFromResponse(resSpItemId);
      }
    } catch (err) {
      // Handle 400 (missing parameter) gracefully
      if (err && typeof err === 'object' && 'response' in err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const axiosError = err as any;
        if (axiosError.response?.status === 400) {
          console.warn('DocumentComments - 400 error (missing parameter):', {
            spItemId: fileMetadata.spItemId,
            error: axiosError.response?.data
          });
          setError(null); // Don't show error for missing parameter, just show empty
          setComments([]);
          return;
        }
      }
      console.error('DocumentComments - Error loading comments:', {
        error: err,
        spItemId: fileMetadata.spItemId,
        siteId: fileMetadata.siteId,
        driveId: fileMetadata.driveId,
        itemId: fileMetadata.itemId,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errorResponse: err && typeof err === 'object' && 'response' in err ? (err as any).response?.data : null
      });
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const getActionTypeColor = (actionType: string): 'primary' | 'success' | 'warning' | 'error' | 'default' => {
    switch (actionType.toUpperCase()) {
      case 'REVIEW':
        return 'primary';
      case 'APPROVE':
      case 'APPROVAL':
        return 'success';
      case 'REJECT':
        return 'error';
      case 'REVISE':
      case 'REVISION':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getActionTypeLabel = (actionType: string): string => {
    switch (actionType.toUpperCase()) {
      case 'REVIEW':
        return 'REVIEW';
      case 'APPROVE':
      case 'APPROVAL':
        return 'APPROVE';
      case 'REJECT':
        return 'REJECT';
      case 'REVISE':
      case 'REVISION':
        return 'REVISE';
      default:
        return actionType.toUpperCase();
    }
  };

  const getUserInitials = (displayName: string | null | undefined, email: string): string => {
    if (displayName) {
      const parts = displayName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return displayName.substring(0, 2).toUpperCase();
    }
    return (email || '?').substring(0, 2).toUpperCase();
  };

  const getCommentAuthor = (comment: DocumentComment): string =>
    comment.commentedBy?.displayName || comment.commentedBy?.email || 'Unknown';

  if (!file) {
    return null;
  }

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Comment sx={{ mr: 1 }} />
          <Typography variant="subtitle1">
            Comments ({comments.length})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {versionLabels.length > 0 && (
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel id="comments-version-label">Show comments for</InputLabel>
            <Select
              labelId="comments-version-label"
              value={effectiveVersionLabel ?? ''}
              label="Show comments for"
              onChange={(e) => setSelectedVersionLabel(e.target.value === '' ? null : e.target.value)}
            >
              <MenuItem value="">Current version</MenuItem>
              {versionLabels.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No comments yet
          </Typography>
        ) : (
          <List>
            {comments.map((comment, idx) => (
              <ListItem
                key={comment.commentedAt ? `${comment.actionId}-${comment.commentedAt}-${idx}` : idx}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderLeft: `4px solid`,
                  borderColor: getActionTypeColor(comment.actionType) === 'primary' ? 'primary.main' :
                              getActionTypeColor(comment.actionType) === 'success' ? 'success.main' :
                              getActionTypeColor(comment.actionType) === 'warning' ? 'warning.main' :
                              getActionTypeColor(comment.actionType) === 'error' ? 'error.main' : 'grey.500',
                  mb: 1,
                  pl: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, width: '100%' }}>
                  <Chip
                    label={getActionTypeLabel(comment.actionType)}
                    color={getActionTypeColor(comment.actionType)}
                    size="small"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      {getUserInitials(
                        comment.commentedBy?.displayName,
                        comment.commentedBy?.email ?? ''
                      )}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary">
                      {getCommentAuthor(comment)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      • {format(new Date(comment.commentedAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {(comment.comments != null && comment.comments !== '' && String(comment.comments).trim() !== 'undefined') ? comment.comments : 'no comment entered'}
                </Typography>
              </ListItem>
            ))}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
